import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { CalendarDays, Clock, Video, MessageCircle } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import ScrollReveal from "@/components/common/ScrollReveal";
import SectionHeading from "@/components/common/SectionHeading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FORMS } from "@/constants/testIds";
import { postForm } from "@/lib/api";
import { sendConsultationEmail } from "@/lib/consultationEmail";

const sessions = [
    { id: "30-min", Icon: Clock, tKey: "consultation.sessions.discovery.title", dKey: "consultation.sessions.discovery.description", priceKey: "consultation.sessions.discovery.price" },
    { id: "60-min", Icon: Video, tKey: "consultation.sessions.deepDive.title", dKey: "consultation.sessions.deepDive.description", priceKey: "consultation.sessions.deepDive.price" },
    { id: "export-audit", Icon: CalendarDays, tKey: "consultation.sessions.audit.title", dKey: "consultation.sessions.audit.description", priceKey: "consultation.sessions.audit.price" },
    { id: "sourcing", Icon: MessageCircle, tKey: "consultation.sessions.sourcing.title", dKey: "consultation.sessions.sourcing.description", priceKey: "consultation.sessions.sourcing.price" },
    { id: "market-entry", Icon: CalendarDays, tKey: "consultation.sessions.marketEntry.title", dKey: "consultation.sessions.marketEntry.description", priceKey: "consultation.sessions.marketEntry.price" },
];

const initial = { name: "", email: "", phone: "", company: "", country: "", session_type: "30-min", preferred_date: "", preferred_time: "", topic: "", notes: "" };

export default function Consultation() {
    const { t } = useTranslation();
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await postForm("/consultation", form);
            await sendConsultationEmail(form);
            toast.success(t("toast.consultationBooked"));
            setForm(initial);
        } catch { toast.error(t("toast.genericError")); }
        finally { setLoading(false); }
    };
    return (
        <>
            <SEO
                title="Book a Consultation — Export & Sourcing Advisory | Exportfy.world"
                description="Book a 30-minute discovery call, 60-minute deep dive, export audit, sourcing consultation, or market-entry strategy session with the Exportfy.world team."
                path="/consultation"
            />
            <PageHero
                eyebrow={t("consultation.hero.eyebrow")}
                title={t("consultation.hero.title")}
                subtitle={t("consultation.hero.subtitle")}
            />

            <section className="section-y">
                <div className="container-x">
                    <ScrollReveal><SectionHeading eyebrow={t("consultation.section.eyebrow")} title={t("consultation.section.title")} /></ScrollReveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        {sessions.map(({ id, Icon, tKey, dKey, priceKey }) => (
                            <button
                                key={id}
                                onClick={() => setForm({ ...form, session_type: id })}
                                className={`text-left rounded-2xl p-6 border transition-all ${form.session_type === id ? "border-[#1D4ED8] bg-[#F5F8FF] shadow-lg shadow-blue-500/10 -translate-y-0.5" : "border-slate-200 bg-white hover:border-slate-300"}`}
                            >
                                <div className={`w-10 h-10 rounded-xl ${form.session_type === id ? "bg-[#1D4ED8] text-white" : "bg-slate-100 text-[#0A2540]"} flex items-center justify-center`}>
                                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                                </div>
                                <div className="mt-4 font-bold text-[#0A2540]">{t(tKey)}</div>
                                <p className="mt-2 text-xs text-slate-500 leading-relaxed">{t(dKey)}</p>
                                <div className="mt-4 text-xs font-bold text-emerald-700">{t(priceKey)}</div>
                            </button>
                        ))}
                    </div>

                    <ScrollReveal delay={80}>
                        <form onSubmit={submit} className="mt-14 bg-white rounded-2xl border border-slate-200 shadow-lg shadow-slate-900/5 p-8 max-w-3xl">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input required placeholder={t("consultation.form.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={FORMS.consultation.name} />
                                <Input required type="email" placeholder={t("consultation.form.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={FORMS.consultation.email} />
                                <Input placeholder={t("consultation.form.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid={FORMS.consultation.phone} />
                                <Input placeholder={t("consultation.form.company")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid={FORMS.consultation.company} />
                                <Input placeholder={t("consultation.form.country")} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid={FORMS.consultation.country} />
                                <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })}>
                                    <SelectTrigger data-testid={FORMS.consultation.sessionType}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {sessions.map((s) => <SelectItem key={s.id} value={s.id}>{t(s.tKey)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Input type="date" placeholder={t("consultation.form.date")} value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid={FORMS.consultation.date} />
                                <Input type="time" placeholder={t("consultation.form.time")} value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} data-testid={FORMS.consultation.time} />
                            </div>
                            <Input className="mt-4" placeholder={t("consultation.form.topic")} value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid={FORMS.consultation.topic} />
                            <Textarea rows={4} className="mt-4" placeholder={t("consultation.form.notes")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid={FORMS.consultation.notes} />
                            <Button type="submit" disabled={loading} data-testid={FORMS.consultation.submit} className="mt-6 rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold px-8 py-6">
                                {loading ? t("consultation.form.booking") : t("consultation.form.confirm")}
                            </Button>
                            <p className="mt-3 text-xs text-slate-500">{t("consultation.form.note")}</p>
                        </form>
                    </ScrollReveal>
                </div>
            </section>
        </>
    );
}
